import { PALETTE, SPEED_LABEL, SPEED_COLOR, GAMEOVER_LOBBY_DELAY_MS, SPECTATOR_JOIN_NOTICE_MS, UI_COLOR, randomBotName } from '@shared/constants.js';
import { cycleDebugUnlock, getAvailableSpeeds } from '@client/unlocks.js';
import { Role, ScreenState } from '@shared/types.js';
import { game, isMobile } from '@client/state.js';
import type { DomEls } from '@client/state.js';

let els: DomEls;
let spectatorNoticeTimer: ReturnType<typeof setTimeout> | null = null;

/** Create an SVG crown badge for the host */
export function createCrownBadge(): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', '14');
  svg.setAttribute('height', '14');
  svg.classList.add('host-crown');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', 'M2 20h20v2H2zm1-4l3-10 6 6 6-6 3 10z');
  path.setAttribute('fill', '#ff0');
  svg.appendChild(path);
  return svg;
}

/** Create an SVG bot icon badge, matching host-crown style */
export function createBotBadge(): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '4 1 16 18');
  svg.setAttribute('width', '14');
  svg.setAttribute('height', '14');
  svg.classList.add('bot-badge');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  // Robot head: antenna, head rectangle, two eyes
  path.setAttribute('d', 'M12 2a1 1 0 011 1v2h4a2 2 0 012 2v8a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2h4V3a1 1 0 011-1zM9 9a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm6 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3z');
  path.setAttribute('fill', '#0ff');
  svg.appendChild(path);
  return svg;
}

export function setupUI(domEls: DomEls, onLocalStart: () => void): void {
  els = domEls;

  els.countMinus.addEventListener('click', (e) => { e.stopPropagation(); setPlayerCount(game.playerCount - 1); });
  els.countPlus.addEventListener('click', (e) => { e.stopPropagation(); setPlayerCount(game.playerCount + 1); });
  els.optionsBtnAction.addEventListener('click', (e) => { e.stopPropagation(); els.optionsPanel.style.display = els.optionsPanel.style.display === 'block' ? 'none' : 'block'; });
  els.optionsPanel.addEventListener('click', (e) => e.stopPropagation());
  els.startBtn.addEventListener('click', (e) => { e.stopPropagation(); onLocalStart(); });
  els.arenaPanel.addEventListener('click', (e) => e.stopPropagation());
  els.speedToggle.addEventListener('click', (e) => { e.stopPropagation(); cycleSpeed(); });

  ensureBotNames();

  // Unlock toggle — host can enable any unlock for all human players
  const unlockToggle = document.getElementById('debug-unlock-toggle')!;
  unlockToggle.addEventListener('click', (e) => {
    e.stopPropagation();
    const { label, color } = cycleDebugUnlock();
    unlockToggle.textContent = label;
    unlockToggle.style.color = color;
    unlockToggle.style.borderColor = color;
  });

  renderOptions();
}

interface ScreenOpts {
  msg?: string;
  sub?: string;
  color?: string;
}

export function setScreen(newState: ScreenState, opts: ScreenOpts = {}): void {
  // Anchor the "YOU" indicator's cycle count at the moment the countdown begins
  // (setScreen fires per countdown step, so only on the first entry)
  if (newState === ScreenState.COUNTDOWN && game.state !== ScreenState.COUNTDOWN) {
    game.youIndicatorStart = performance.now();
  }
  game.state = newState;
  if (game.tickTimer) { clearInterval(game.tickTimer); game.tickTimer = null; }
  // A pending entry-notice dismissal belongs to the previous screen — never the new one
  if (spectatorNoticeTimer) { clearTimeout(spectatorNoticeTimer); spectatorNoticeTimer = null; }

  const showHeader = newState !== ScreenState.HOME && newState !== ScreenState.ARENA && newState !== ScreenState.LOCAL_SETUP && newState !== ScreenState.RECONNECTING;
  const showHint = newState === ScreenState.HOME || newState === ScreenState.PAUSED;
  els.header.style.display = showHeader ? '' : 'none';
  els.hint.classList.toggle('hidden', !showHint);
  els.version.style.display = newState === ScreenState.HOME ? '' : 'none';
  els.pauseBtn.style.display = (newState === ScreenState.PLAYING && !game.online) ? 'flex' : 'none';

  els.optionsPanel.style.display = 'none';
  els.arenaPanel.style.display = 'none';
  els.joinArenaBtn.style.display = 'none';
  els.localPlayBtn.style.display = 'none';
  els.arenaActionButtons.style.display = 'none';
  els.arenaStartRow.style.display = 'none';
  els.arenaHomeBtn.style.display = 'none';
  els.nameInput.style.display = 'none';
  els.onlineStatus.style.visibility = 'hidden';
  els.message.style.display = '';
  els.subMessage.style.display = '';
  els.spectatingLabel.classList.remove('active');

  // Always hide nav overlay on screen transitions
  els.navOverlay.classList.remove('active');

  // Section dividers only on home screen
  const sectionDividers = document.querySelectorAll('.section-divider');
  sectionDividers.forEach(el => (el as HTMLElement).style.display = newState === ScreenState.HOME ? '' : 'none');

  // D-pad: show during gameplay states on mobile (keep visible during pause/gameover to prevent layout shift)
  const dpadStates: ScreenState[] = [ScreenState.PLAYING, ScreenState.COUNTDOWN, ScreenState.PAUSED, ScreenState.GAMEOVER, ScreenState.ARENA_GAMEOVER];
  const showDpad = dpadStates.includes(newState) && isMobile && game.onlineRole !== Role.SPECTATOR;
  els.dpadArea.classList.toggle('active', showDpad);
  // Clear stale highlight animations when d-pad appears
  if (showDpad) {
    els.dpadArea.querySelectorAll('.highlight').forEach(el => el.classList.remove('highlight'));
  }

  switch (newState) {
    case ScreenState.HOME:
      els.overlay.classList.add('active');
      els.message.textContent = 'TRON';
      els.message.style.color = '#0ff';
      els.subMessage.textContent = '';
      els.subMessage.style.display = 'none';
      els.joinArenaBtn.style.display = '';
      els.localPlayBtn.style.display = '';
      els.nameInput.style.display = '';
      els.onlineStatus.style.visibility = '';
      els.hint.textContent = isMobile ? 'SWIPE TO STEER' : 'WASD / ARROW KEYS  |  SPACE: LOCAL START';
      break;

    case ScreenState.LOCAL_SETUP:
      els.overlay.classList.add('active');
      els.message.textContent = 'LOCAL';
      els.message.style.color = '#0ff';
      els.subMessage.style.display = 'none';
      els.optionsPanel.style.display = 'block';
      document.getElementById('debug-unlock-row')!.style.display = 'flex';
      break;

    case ScreenState.COUNTDOWN:
      showOverlay(opts.msg ?? '', '', opts.color ?? '#0ff');
      els.subMessage.style.display = 'none';
      break;

    case ScreenState.PLAYING:
      els.overlay.classList.remove('active');
      break;

    case ScreenState.PAUSED:
      showOverlay('PAUSED', isMobile ? 'TAP TO RESUME' : 'SPACE OR ESC TO RESUME', '#0ff');
      showActionButtons(false, false, true);
      break;

    case ScreenState.GAMEOVER:
      showOverlay(opts.msg ?? 'DRAW', opts.sub ?? '', opts.color ?? '#fff');
      if (opts.sub) {
        els.subMessage.style.display = '';
      } else {
        els.subMessage.style.display = 'none';
      }
      showActionButtons(true, false, true, true);
      break;

    case ScreenState.ARENA:
      els.overlay.classList.add('active');
      els.message.textContent = 'ARENA';
      els.message.style.color = '#0ff';
      els.subMessage.style.display = 'none';
      els.arenaPanel.style.display = 'block';
      els.arenaHomeBtn.style.display = '';
      // Buttons inside arena panel (start row), not in action-buttons
      break;

    case ScreenState.SPECTATING:
      els.spectatingLabel.classList.add('active');
      if (opts.msg) {
        // Entry notice (e.g. joined mid-game) — show briefly, then reveal the game.
        // Timer is cleared on any screen transition, so it can only dismiss its own notice.
        showOverlay(opts.msg, opts.sub ?? '', opts.color ?? UI_COLOR.WARNING);
        spectatorNoticeTimer = setTimeout(() => {
          spectatorNoticeTimer = null;
          els.overlay.classList.remove('active');
        }, SPECTATOR_JOIN_NOTICE_MS);
      } else {
        els.overlay.classList.remove('active');
      }
      break;

    case ScreenState.ARENA_GAMEOVER: {
      const delaySec = Math.round(GAMEOVER_LOBBY_DELAY_MS / 1000);
      showOverlay(opts.msg ?? 'DRAW', `Arena in ${delaySec}...`, opts.color ?? '#fff');
      els.subMessage.style.color = '#888';
      els.subMessage.style.fontSize = '1.5em';
      showActionButtons(false, false, true);
      let remaining = delaySec - 1;
      const countdownTimer = setInterval(() => {
        if (remaining <= 0 || game.state !== ScreenState.ARENA_GAMEOVER) {
          clearInterval(countdownTimer);
          return;
        }
        els.subMessage.textContent = `Arena in ${remaining}...`;
        remaining--;
      }, 1000);
      break;
    }

    case ScreenState.RECONNECTING:
      showOverlay('RECONNECTING...', opts.sub ?? '', '#ff0');
      showActionButtons(false, false, true);
      break;
  }
}

function showActionButtons(playAgain: boolean, spectate: boolean, home: boolean, options = false): void {
  els.arenaActionButtons.style.display = '';
  els.arenaActionButtons.classList.remove('row');
  els.playAgainBtn.classList.toggle('visible', playAgain);
  els.spectateBtn.classList.toggle('visible', spectate);
  els.optionsBtnAction.classList.toggle('visible', options);
  els.homeBtn.classList.toggle('visible', home);
}

function showOverlay(msg: string, sub: string, color: string): void {
  els.message.textContent = msg;
  els.message.style.color = color;
  els.subMessage.textContent = sub;
  els.subMessage.style.display = '';
  els.overlay.classList.add('active');
}

export function updateScoresUI(): void {
  els.header.innerHTML = '';
  const h1 = document.createElement('h1');
  h1.textContent = 'TRON';
  els.header.appendChild(h1);
  for (let i = 0; i < game.players.length; i++) {
    const span = document.createElement('span');
    span.className = 'score';
    span.style.color = game.players[i].color || PALETTE[i]?.color || '#888';
    const p = game.players[i];
    const isLocalPlayer = game.online && p.id === game.onlinePlayerId;
    const label = isLocalPlayer ? 'You' : (p.name || `P${i + 1}`);
    // Name lives in its own element so it can ellipsis-truncate while the
    // bot/crown badges stay fully visible beside it (#11).
    const nameEl = document.createElement('span');
    nameEl.className = 'score-name';
    nameEl.textContent = `${label}: ${game.scores[p.id] || 0}`;
    span.appendChild(nameEl);
    if (p.isBot) {
      span.appendChild(createBotBadge());
    }
    if (game.online && p.id === game.onlinePlayerId && game.isHost) {
      span.appendChild(createCrownBadge());
    }
    els.header.appendChild(span);
  }
}

function renderOptions(): void {
  els.countDisplay.textContent = String(game.playerCount);
  els.playerList.innerHTML = '';
  const enteredName = els.nameInput.value.trim();
  const humanCount = game.playerIsHuman.slice(0, game.playerCount).filter(Boolean).length;
  let humanIdx = 0;
  for (let i = 0; i < game.playerCount; i++) {
    const row = document.createElement('div');
    row.className = 'opt-row';

    const label = document.createElement('span');
    label.className = 'opt-label';
    const dot = document.createElement('span');
    dot.className = 'color-dot';
    dot.style.background = PALETTE[i].color;
    label.appendChild(dot);

    let displayName: string;
    if (game.playerIsHuman[i]) {
      if (humanCount === 1) {
        displayName = enteredName || 'You';
      } else {
        displayName = enteredName ? `${enteredName} ${++humanIdx}` : `P${++humanIdx}`;
      }
    } else {
      displayName = game._botNames[i] || `Bot ${i + 1}`;
    }
    label.appendChild(document.createTextNode(displayName));
    row.appendChild(label);

    const btn = document.createElement('button');
    const isHuman = game.playerIsHuman[i];
    btn.className = 'opt-toggle ' + (isHuman ? 'human' : 'ai');
    btn.textContent = isHuman ? 'HUMAN' : 'AI';
    // Only allow toggling to HUMAN if there's no other human (max 1 human in solo)
    const canToggle = isHuman || humanCount === 0;
    if (!canToggle) {
      btn.style.opacity = '0.3';
      btn.style.cursor = 'default';
    }
    btn.addEventListener('click', () => {
      if (!isHuman && humanCount >= 1) return;
      game.playerIsHuman[i] = !game.playerIsHuman[i];
      ensureBotNames();
      renderOptions();
    });
    row.appendChild(btn);

    els.playerList.appendChild(row);
  }

  // Disable +/- buttons at limits
  const minusBtn = els.countMinus as HTMLButtonElement;
  const plusBtn = els.countPlus as HTMLButtonElement;
  minusBtn.disabled = game.playerCount <= 2;
  minusBtn.style.opacity = game.playerCount <= 2 ? '0.3' : '';
  plusBtn.disabled = game.playerCount >= 6;
  plusBtn.style.opacity = game.playerCount >= 6 ? '0.3' : '';
}

function setPlayerCount(n: number): void {
  game.playerCount = Math.max(2, Math.min(6, n));
  ensureBotNames();
  renderOptions();
}

/** Ensure every AI slot has a unique bot name. */
function ensureBotNames(): void {
  const usedNames = new Set(game._botNames.filter(Boolean));
  for (let i = 0; i < game.playerCount; i++) {
    if (!game.playerIsHuman[i] && !game._botNames[i]) {
      let name = randomBotName();
      for (let n = 0; n < 100 && usedNames.has(name); n++) name = randomBotName();
      game._botNames[i] = name;
      usedNames.add(name);
    }
  }
}

function cycleSpeed(): void {
  const speeds = getAvailableSpeeds();
  const idx = speeds.indexOf(game.speed);
  game.speed = speeds[(idx + 1) % speeds.length];
  els.speedToggle.textContent = SPEED_LABEL[game.speed];
  const color = SPEED_COLOR[game.speed];
  els.speedToggle.style.color = color;
  els.speedToggle.style.borderColor = color;
}
